import type { ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { supportsWebGL } from '@/lib/webgl'
import { useInView } from '@/hooks/use-in-view'
import { HERO_RISE, MILESTONES } from '@/lib/ascent-timeline'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { RingsDivider } from '@/components/generals/hairline'

const STILLS = [
    { at: 0.03, milestone: MILESTONES[0] },
    { at: 0.17, milestone: MILESTONES[3] },
    { at: 0.24, milestone: MILESTONES[4] },
    { at: 0.338, milestone: MILESTONES[5] },
    { at: 0.54, milestone: MILESTONES[8] },
    { at: 0.79, milestone: MILESTONES[13] },
    { at: 0.93, milestone: MILESTONES[15] }
]

/** With reduced motion, the ascent becomes a calm sequence of still frames. */
export const AscentStills = (): ReactNode => {
    const { ref, inView } = useInView<HTMLElement>({ rootMargin: '600px 0px' })
    const [images, setImages] = useState<string[] | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!inView || images || failed) return

        if (!supportsWebGL()) {
            setFailed(true)
            return
        }

        let cancelled = false

        const render = async () => {
            try {
                const { createHeroEngine } = await import('@/lib/three/hero-engine')
                const canvas = document.createElement('canvas')

                canvas.width = 1280
                canvas.height = 800

                const engine = await createHeroEngine({ canvas, mobile: false, interactive: false })
                let urls: string[] = []

                try {
                    await engine.preload()

                    urls = STILLS.map(still => {
                        engine.renderAt(HERO_RISE + still.at * (1 - HERO_RISE))

                        return canvas.toDataURL('image/jpeg', 0.86)
                    })
                }
                finally {
                    engine.dispose()
                }

                if (!cancelled) setImages(urls)
            }
            catch {
                if (!cancelled) setFailed(true)
            }
        }

        void render()

        return () => {
            cancelled = true
        }
    }, [inView, images, failed])

    return (
        <section ref={ref} id='the-long-way-up' aria-labelledby='long-way-title' className='relative py-28 md:py-40'>
            <div className='container-page'>
                <p className='label-mono text-brass'>
                    The long way up
                </p>
                <RevealLines
                    as='h2'
                    id='long-way-title'
                    lines={['From the dome', 'to the edge of the galaxy.']}
                    className='display-serif mt-5 text-5xl text-star md:text-7xl'
                />
                <Reveal variant='wipe' className='mt-6 max-w-xl text-star-2'>
                    <p>
                        Seven still frames from one journey outward, from the hill above Exmoor to a view of the whole Milky Way.
                    </p>
                </Reveal>

                <ol className='mt-20 space-y-24 md:space-y-32'>
                    {STILLS.map((still, index) => (
                        <li key={still.at}>
                            <Reveal as='figure' className={cn('grid items-end gap-8 md:grid-cols-12', index % 2 === 1 && 'md:[direction:rtl]')}>
                                <div className='media media-grain aspect-[16/10] bg-ink-2 md:col-span-8 [direction:ltr]'>
                                    {images ? (
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img
                                            src={images[index]}
                                            alt={`${still.milestone.distance}: ${still.milestone.line}`}
                                            className='absolute inset-0 h-full w-full object-cover'
                                        />
                                    ) : (
                                        <div className='absolute inset-0 flex items-center justify-center'>
                                            <p className='label-mono text-star-3'>
                                                {failed ? still.milestone.distance : 'Rendering the view'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <figcaption className='md:col-span-4 [direction:ltr]'>
                                    <p className='label-mono text-brass'>
                                        {`${String(index + 1).padStart(2, '0')} / ${still.milestone.distance}`}
                                    </p>
                                    <p className='mt-4 font-heading text-3xl font-light leading-snug text-star'>
                                        {still.milestone.line}
                                    </p>
                                </figcaption>
                            </Reveal>
                        </li>
                    ))}
                </ol>

                <RingsDivider className='mt-28' label='Then, nothing but stars' />
            </div>
        </section>
    )
}
